/**
 * Typed BFF client, generated end-to-end from the OpenAPI contract
 * (app/openapi.yaml -> schema.d.ts via `npm run openapi:types`).
 *
 * This is the sanctioned way for the SPA to call the BFF: request/response
 * shapes are inferred from the contract, so a contract change surfaces as a
 * compile error here. New screens should use `api`; existing `fetch` call sites
 * are being migrated to it (see BACKLOG).
 *
 * `baseUrl: '/api'` matches the contract's server basePath, so paths are keyed
 * without the prefix (e.g. `api.GET('/session')`). Same-origin cookie session
 * is sent on every call.
 */
import createClient from 'openapi-fetch';
import type { paths } from './schema.js';
import { noteDuplicateSessionFrom } from '../duplicate-session-store.js';
import { noteUnauthenticatedFrom } from '../auth-store.js';
import { noteReconnectNeededFrom } from '../genesys-reconnect-store.js';

export const api = createClient<paths>({
  // Absolute base (same origin) so URL construction works under both the browser
  // and the jsdom/node test runtime, where a bare relative base fails to parse.
  baseUrl: `${location.origin}/api`,
  credentials: 'include',
  // Defer to the live global fetch on every call (rather than capturing it at
  // creation) so test fetch spies are honoured. openapi-fetch passes a built
  // Request object.
  fetch: (request) => globalThis.fetch(request),
});

// CCF-13 (#413): watch every BFF response for the duplicate-session freeze. The
// BFF returns 423 `{ error: 'duplicate_session_block', block }` on every route
// once an ONLINE duplicate session is detected; this middleware records the
// block into the observable store so the app renders the freeze overlay the
// instant ANY call is refused, without polling. Read-only — it clones the
// response, so the calling code still gets the untouched body.
api.use({
  async onResponse({ response, schemaPath }) {
    void noteDuplicateSessionFrom(response);
    // #431 (bucket 1): a 401 on any authenticated call means the session was
    // invalidated (e.g. logged out in another tab) — flip the auth store so the
    // app drops to sign-in immediately, without waiting for a refocus/refresh.
    // EXCLUDE `/session` itself: `fetchSession` already handles its 401 (embedded
    // → NotAuthenticatedError; standalone → redirect), and signalling here would
    // re-trigger the session re-fetch that produced it — an infinite loop.
    if (schemaPath !== '/session') noteUnauthenticatedFrom(response);
    // #628 (increment B): watch for the online-only-Offline 403 signal
    // (`onlineWouldAllow: true`) — an online-only action refused ONLY because the
    // Genesys online link dropped mid-call. Record it so the app can offer a
    // "Reconnect to Genesys" recovery affordance instead of a bare permission
    // error. Read-only (clones the response); never grants online state.
    void noteReconnectNeededFrom(response);
    return undefined;
  },
});
